import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { findNextPending, readNavContext } from './reclamacoesNav';
import './Reclamacoes.css';

/**
 * Revisão de UMA reclamação (VAR), em etapas: Perguntas → Resposta → Resumo → Enviar.
 *
 * - Nada vai para a API antes do "Enviar revisão" (POST /points-review/disputes/submit, que grava
 *   perguntas + resposta + marca de revisado numa transação e recusa se alguém já revisou).
 * - Rascunho no localStorage por atendimento (expira em 7 dias, no máximo 20) — sair, recarregar
 *   ou voltar não perde o trabalho; "Descartar alterações" apaga.
 * - Só "Aplicável" e "Nota" são editáveis; "pontua" e "pontos" vêm da avaliação (congelados na
 *   hora em que a IA avaliou — é o cálculo que valia quando o vendedor reclamou).
 * - Atendimento já revisado abre as mesmas telas em só leitura.
 */

const STEPS = [
    { key: 'perguntas', label: '1. Perguntas' },
    { key: 'resposta', label: '2. Resposta ao vendedor' },
    { key: 'resumo', label: '3. Resumo e envio' },
];
const STATUS_LABEL = { pending: 'Pendente', granted: 'Concedida', denied: 'Negada', partial: 'Parcial' };
const CHILD_LABELS = {
    transcriptions_classification: 'Classificação',
    transcriptions_feedback: 'Feedback',
    transcriptions_summary: 'Resumo',
    transcriptions_products: 'Produtos',
    transcriptions_objections: 'Objeções',
    transcriptions_lost_sales_reasons: 'Motivos de perda de venda',
    transcriptions_conversion: 'Conversão',
};
const HIDDEN_CHILDREN = new Set(['transcriptions_sales_evaluation', 'transcriptions_markup', 'transcriptions_audit']);

// ---------------------------------------------------------------- rascunho (localStorage)
const DRAFT_PREFIX = 'vr-draft:v1:';
const DRAFT_TTL_MS = 7 * 24 * 3600 * 1000;
const DRAFT_MAX = 20;
const draftKey = (sd, tid) => `${DRAFT_PREFIX}${sd}:${tid}`;
function pruneDrafts() {
    try {
        const all = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (!k || !k.startsWith(DRAFT_PREFIX)) continue;
            let at = 0;
            try { at = JSON.parse(localStorage.getItem(k) || '{}').updatedAt || 0; } catch (e) { at = 0; }
            all.push({ k, at });
        }
        const now = Date.now();
        all.filter((d) => now - d.at > DRAFT_TTL_MS).forEach((d) => localStorage.removeItem(d.k));
        all.filter((d) => now - d.at <= DRAFT_TTL_MS).sort((a, b) => b.at - a.at)
            .slice(DRAFT_MAX).forEach((d) => localStorage.removeItem(d.k));
    } catch (e) { /* storage indisponível: segue sem rascunho */ }
}
function readDraft(sd, tid) {
    try {
        const d = JSON.parse(localStorage.getItem(draftKey(sd, tid)) || 'null');
        if (d && Date.now() - (d.updatedAt || 0) <= DRAFT_TTL_MS) return d;
    } catch (e) { /* ignore */ }
    return null;
}
function writeDraft(sd, tid, draft) {
    try { localStorage.setItem(draftKey(sd, tid), JSON.stringify({ ...draft, updatedAt: Date.now() })); } catch (e) { /* ignore */ }
}
function clearDraft(sd, tid) {
    try { localStorage.removeItem(draftKey(sd, tid)); } catch (e) { /* ignore */ }
}

// ---------------------------------------------------------------- utilitários
const brDateTime = (iso) => {
    if (!iso) return '—';
    const s = String(iso);
    const dt = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z');
    if (isNaN(dt)) return s;
    return dt.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo',
    });
};
const fmt = (v) => (v === null || v === undefined || Number.isNaN(Number(v)) ? '—'
    : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 }));
const earned = (cond, score, earn, pts) => (earn === true && cond !== false && score !== null && score !== undefined
    && pts !== null && pts !== undefined ? Number(pts) * Number(score) : 0);

// "00m55s speaker_1: texto" -> { time: "00:55", speaker: 1, text }
const TX_LINE = /^\s*(\d+)m(\d{1,2})s\s+(?:speaker_(\d+)|([^:]{1,40})):\s?(.*)$/;
function parseTranscript(text) {
    const out = [];
    let matched = 0;
    for (const line of String(text || '').split(/\r?\n/)) {
        if (!line.trim()) continue;
        const m = TX_LINE.exec(line);
        if (m) {
            matched += 1;
            out.push({ time: `${m[1].padStart(2, '0')}:${m[2].padStart(2, '0')}`,
                speaker: m[3] !== undefined ? Number(m[3]) : null, label: m[4] || null, text: m[5] });
        } else if (out.length) {
            out[out.length - 1].text += `\n${line}`;
        } else {
            out.push({ time: null, speaker: null, label: null, text: line });
        }
    }
    return matched ? out : null;
}

// Feedback da IA: string (texto corrido) ou objeto {good, bad} — este vira dois boxes,
// verde e vermelho. Aceita também o objeto vindo como string JSON.
function AiFeedback({ value, empty }) {
    let v = value;
    if (typeof v === 'string' && /^\s*\{/.test(v)) {
        try { v = JSON.parse(v); } catch (e) { /* fica como texto */ }
    }
    if (v && typeof v === 'object' && !Array.isArray(v) && ('good' in v || 'bad' in v)) {
        const txt = (x) => (typeof x === 'string' ? x.trim() : x == null ? '' : JSON.stringify(x, null, 2));
        return (
            <>
                <div className="vr-ai-box good"><div className="vr-ai-box-title">👍 Pontos positivos (good)</div>
                    {txt(v.good) || <em className="vr-muted">—</em>}</div>
                <div className="vr-ai-box bad"><div className="vr-ai-box-title">👎 Pontos a melhorar (bad)</div>
                    {txt(v.bad) || <em className="vr-muted">—</em>}</div>
            </>
        );
    }
    const text = typeof v === 'string' ? v.trim() : v == null ? '' : JSON.stringify(v, null, 2);
    if (!text) return <div className="vr-ai-text empty"><em className="vr-muted">{empty}</em></div>;
    return <div className="vr-ai-text">{text}</div>;
}

function Info({ text }) {
    if (!text) return null;
    return <span className="vr-info">i<span className="vr-tip">{text}</span></span>;
}

function Switch({ on, onChange, disabled, label }) {
    return (
        <span className={`vr-switch ${on ? 'on' : ''} ${disabled ? 'disabled' : ''}`}
            role="switch" aria-checked={!!on} tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && onChange(!on)}
            onKeyDown={(e) => { if (!disabled && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); onChange(!on); } }}>
            <span className="vr-switch-track" />
            {label}
        </span>
    );
}

function Modal({ title, children, footer, large, wide, onClose }) {
    return (
        <div className="vr-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
            <div className={`vr-modal ${large ? 'lg' : wide ? 'md' : ''}`}>
                <div className="vr-modal-head">{title}</div>
                <div className="vr-modal-body">{children}</div>
                <div className="vr-modal-foot">{footer}</div>
            </div>
        </div>
    );
}

function renderChild(child, val) {
    const inner = (val && typeof val === 'object' && 'data' in val) ? val.data : val;
    if (child === 'transcriptions_feedback') {
        const fb = inner && typeof inner === 'object' ? (inner.feedback ?? inner) : inner;
        if (fb && typeof fb === 'object' && ('good' in fb || 'bad' in fb)) {
            return (<>
                <div className="vr-label">👍 Pontos positivos</div><div className="vr-box">{fb.good ? String(fb.good).trim() : '—'}</div>
                <div className="vr-label">👎 Pontos a melhorar</div><div className="vr-box">{fb.bad ? String(fb.bad).trim() : '—'}</div>
            </>);
        }
    }
    if (child === 'transcriptions_summary') {
        const txt = inner && typeof inner === 'object' ? (inner.data ?? inner.summary) : inner;
        if (typeof txt === 'string') return <div className="vr-box">{txt}</div>;
    }
    if (child === 'transcriptions_classification' && inner && typeof inner === 'object' && !Array.isArray(inner)) {
        return (
            <table className="vr-table"><tbody>
                {Object.entries(inner).map(([k, v]) => (
                    <tr key={k}><td className="vr-muted">{k}</td><td>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</td></tr>
                ))}
            </tbody></table>
        );
    }
    return <pre className="vr-pre">{JSON.stringify(inner, null, 2)}</pre>;
}

// ---------------------------------------------------------------- tela
function ReclamacaoRevisao({ user }) {
    const userId = user?.user_id;
    const { subdomain: sd, transcriptionId, step = 'perguntas' } = useParams();
    const tid = Number(transcriptionId);
    const navigate = useNavigate();

    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4500);
    }, []);

    const [kase, setKase] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [draft, setDraft] = useState({ items: {}, answer: null, stage: 'perguntas' });
    const [expanded, setExpanded] = useState({});
    const [qFilter, setQFilter] = useState('claimed');
    const [side, setSide] = useState('tx');            // painel à esquerda, sempre aberto: 'tx' | 'ai' | 'data'
    const [txData, setTxData] = useState(undefined);   // undefined = não carregado
    const [extData, setExtData] = useState(undefined);
    const [openChild, setOpenChild] = useState({});
    const [audioUrl, setAudioUrl] = useState(null);
    const [modal, setModal] = useState(null);           // {kind, ...}
    const [summaryTab, setSummaryTab] = useState('beh');
    const [sending, setSending] = useState(false);
    const stickyRef = useRef(null);
    const [stickyH, setStickyH] = useState(0);
    const [viewH, setViewH] = useState(0);     // altura visível do container que rola (.app-main)
    const [footerH, setFooterH] = useState(0);
    const footerRO = useRef(null);
    const footerRef = useCallback((el) => {
        if (footerRO.current) footerRO.current.disconnect();
        if (!el) { setFooterH(0); return; }
        const ro = new ResizeObserver(() => setFooterH(el.offsetHeight));
        ro.observe(el);
        footerRO.current = ro;
    }, []);

    const readOnly = !!kase?.is_reviewed;

    // ---- carga
    const load = useCallback(async () => {
        try {
            const r = await apiService.pointsDisputeCase({ userId, subdomain: sd, transcription_id: tid });
            if (!r || r.success !== true || !('transcription_id' in r)) throw new Error('resposta inesperada');
            setKase(r);
            if (r.kind !== 'dispute') setQFilter('all');
            if (!r.is_reviewed) {
                const d = readDraft(sd, tid);
                if (d) setDraft({ items: d.items || {}, answer: d.answer ?? null, stage: d.stage || 'perguntas' });
            }
        } catch (e) {
            setLoadError(e.response?.data?.detail || e.message || 'Erro ao abrir a reclamação');
        }
    }, [userId, sd, tid]);
    useEffect(() => { pruneDrafts(); if (userId) load(); }, [userId, load]);

    useEffect(() => {
        if (!stickyRef.current) return undefined;
        // o sticky é relativo ao container que rola (o .app-main do layout), não à janela
        const scroller = stickyRef.current.closest('.app-main') || document.documentElement;
        const ro = new ResizeObserver(() => {
            setStickyH(stickyRef.current ? stickyRef.current.offsetHeight : 0);
            setViewH(scroller.clientHeight);
        });
        ro.observe(stickyRef.current);
        ro.observe(scroller);
        return () => ro.disconnect();
    }, [kase]);

    // ---- rascunho
    const persist = useCallback((next) => {
        setDraft(next);
        if (!readOnly) writeDraft(sd, tid, next);
    }, [sd, tid, readOnly]);
    const setItem = (item, patch) => {
        const cur = draft.items[item] || {};
        const nextItem = { ...cur, ...patch };
        Object.keys(nextItem).forEach((k) => {
            if (nextItem[k] === undefined || nextItem[k] === '' || nextItem[k] === false && k === 'denied') delete nextItem[k];
        });
        const items = { ...draft.items };
        if (Object.keys(nextItem).length) items[item] = nextItem; else delete items[item];
        persist({ ...draft, items });
    };
    const hasItemChanges = Object.keys(draft.items).length > 0;
    const hasAnything = hasItemChanges || !!(draft.answer && draft.answer.trim());

    // ---- dados derivados
    const ev = kase?.evaluation || null;
    const details = useMemo(() => (ev?.result?.data?.evaluation_details || []).filter((q) => q && typeof q === 'object'), [ev]);
    const behMeta = ev?.behaviors || {};
    const claimedKeys = useMemo(() => new Set(((kase?.dispute?.behaviors) || []).map((b) => b.behavior_key).filter(Boolean)), [kase]);

    const qinfo = useCallback((q) => {
        const sp = q.score_prompt || {};
        const rev = ev?.effective_by_item?.[q.item] || ev?.effective_by_item?.[String(q.item)] || null;
        const orig = { cond: q.condition, score: q.condition === false ? null : (sp.score ?? null) };
        const base = {
            cond: rev && rev.new_condition !== null && rev.new_condition !== undefined ? rev.new_condition : q.condition,
        };
        base.score = rev && rev.new_score !== null && rev.new_score !== undefined ? Number(rev.new_score)
            : (base.cond === false ? null : (sp.score ?? null));
        const earn = rev && rev.new_earn_points !== null && rev.new_earn_points !== undefined ? rev.new_earn_points : q.earn_points;
        const pts = rev && rev.new_points !== null && rev.new_points !== undefined ? Number(rev.new_points) : q.points;
        const d = draft.items[q.item] || {};
        let cond; let score;
        if (d.denied) { cond = orig.cond; score = orig.score; } else {
            cond = d.condition !== undefined ? d.condition : base.cond;
            score = d.score !== undefined ? d.score : base.score;
        }
        if (cond === false) score = null;
        const e0 = earned(orig.cond, orig.score, earn, pts);
        const eBase = earned(base.cond, base.score, earn, pts);
        const eNew = earned(cond, score, earn, pts);
        let state = 'none';
        if (readOnly) {
            state = rev ? (eNew > e0 ? 'up' : eNew < e0 ? 'down' : 'justified') : 'none';
        } else if (d.denied) state = 'denied';
        else if (d.condition !== undefined || d.score !== undefined) {
            state = eNew > eBase ? 'up' : eNew < eBase ? 'down' : 'changed';
        } else if (d.justification) state = 'justified';
        return { sp, rev, orig, base, earn, pts, d, cond, score, e0, eBase, eNew, state,
            claimed: !!(q.behavior_key && claimedKeys.has(q.behavior_key)) };
    }, [ev, draft.items, claimedKeys, readOnly]);

    // agrupamento: comportamento (reclamados primeiro) → categoria → peso desc
    const grouped = useMemo(() => {
        const filtered = details.filter((q) => {
            if (qFilter === 'claimed') return q.behavior_key && claimedKeys.has(q.behavior_key);
            if (qFilter === 'behavior') return !!q.behavior_key;
            return true;
        });
        const byBeh = new Map();
        filtered.forEach((q) => {
            const k = q.behavior_key || '';
            if (!byBeh.has(k)) byBeh.set(k, []);
            byBeh.get(k).push(q);
        });
        const name = (k) => ((behMeta[k] || {}).display_name || k || '').toLowerCase();
        const keys = [...byBeh.keys()].sort((a, b) => {
            if (!a) return 1; if (!b) return -1;
            const ca = claimedKeys.has(a) ? 0 : 1; const cb = claimedKeys.has(b) ? 0 : 1;
            return ca - cb || name(a).localeCompare(name(b));
        });
        return keys.map((k) => ({
            key: k,
            items: byBeh.get(k).sort((x, y) => String(x.category || '').localeCompare(String(y.category || ''))
                || Number(y.weight || 0) - Number(x.weight || 0)),
        }));
    }, [details, qFilter, claimedKeys, behMeta]);

    const behaviorSummary = useMemo(() => {
        const acc = {};
        details.forEach((q) => {
            if (!q.behavior_key) return;
            const i = qinfo(q);
            const a = acc[q.behavior_key] || (acc[q.behavior_key] = { key: q.behavior_key, orig: 0, base: 0, now: 0, max: 0 });
            a.orig += i.e0; a.base += i.eBase; a.now += i.eNew;
            if (i.earn === true && i.pts) a.max += Number(i.pts);
        });
        return Object.values(acc).filter((a) => a.max > 0 || a.orig || a.now)
            .sort((x, y) => (claimedKeys.has(y.key) - claimedKeys.has(x.key)) || x.key.localeCompare(y.key));
    }, [details, qinfo, claimedKeys]);

    // ---- painel lateral
    const openSide = async (tab) => {
        setSide(tab);
        if (tab === 'tx' && txData === undefined) {
            try {
                const t = await apiService.pointsTranscription({ userId, subdomain: sd, transcription_id: tid });
                const text = t.anon_transcript || t.anon_context_transcript || '';
                setTxData({ text, parsed: parseTranscript(text), context: !t.anon_transcript && !!t.anon_context_transcript });
            } catch (e) { setTxData({ error: e.response?.data?.detail || 'Erro ao carregar a transcrição' }); }
        }
        if (tab === 'data' && extData === undefined) {
            try {
                const r = await apiService.pointsAttendanceData({ userId, subdomain: sd, transcription_id: tid });
                setExtData({ data: r.data || {}, models: r.models || {} });
            } catch (e) { setExtData({ error: e.response?.data?.detail || 'Erro ao carregar os dados' }); }
        }
    };
    useEffect(() => {
        if (kase && txData === undefined) openSide('tx');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kase]);
    const copyTranscript = async () => {
        const txt = txData && txData.parsed
            ? txData.parsed.map((l) => `${l.time ? `[${l.time}] ` : ''}${l.speaker !== null ? `Falante ${l.speaker + 1}: ` : l.label ? `${l.label}: ` : ''}${l.text}`).join('\n')
            : (txData && txData.text) || '';
        try {
            await navigator.clipboard.writeText(txt);
        } catch (e) {
            const ta = document.createElement('textarea');
            ta.value = txt; document.body.appendChild(ta); ta.select();
            try { document.execCommand('copy'); } catch (e2) { notify('Não foi possível copiar', 'error'); ta.remove(); return; }
            ta.remove();
        }
        notify('Transcrição copiada', 'success');
    };
    const audioMissing = () => notify('Áudio não encontrado para este atendimento.', 'error');
    const loadAudio = async () => {
        if (!kase?.has_audio) { audioMissing(); return; }
        try {
            const r = await apiService.pointsAudio({ userId, subdomain: sd, transcription_id: tid, redacted: !!kase?.restricted_admin });
            if (!r || !r.presigned_url) { audioMissing(); return; }
            setAudioUrl(r.presigned_url);
        } catch (e) { audioMissing(); }
    };

    // ---- navegação
    const goStep = (key) => {
        if (!readOnly) persist({ ...draft, stage: key });
        navigate(`/reclamacoes-pontos/${encodeURIComponent(sd)}/${tid}/${key}`);
        window.scrollTo(0, 0);
    };
    const stageIdx = STEPS.findIndex((s) => s.key === (draft.stage || 'perguntas'));
    const canOpenStep = (key) => readOnly || STEPS.findIndex((s) => s.key === key) <= Math.max(stageIdx, 0);
    useEffect(() => {
        // URL de uma etapa ainda não alcançada (edição): volta para a etapa do rascunho
        if (kase && !readOnly && !canOpenStep(step)) navigate(`/reclamacoes-pontos/${encodeURIComponent(sd)}/${tid}/${draft.stage || 'perguntas'}`, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, kase]);

    // Sair da revisão com rascunho pede confirmação por QUALQUER caminho: chevron, menu lateral,
    // voltar/avançar do navegador (useBlocker, exige o data router do App.js). Trocar de etapa
    // dentro do mesmo atendimento não conta como sair. Fechar a aba/recarregar cai no aviso
    // nativo do navegador (beforeunload) — o rascunho fica salvo de todo jeito.
    const leavingRef = useRef(false);
    const casePrefix = `/reclamacoes-pontos/${encodeURIComponent(sd)}/${tid}/`;
    const guardOn = !readOnly && hasAnything;
    const blocker = useBlocker(({ nextLocation }) => guardOn && !leavingRef.current
        && !nextLocation.pathname.startsWith(casePrefix));
    useEffect(() => {
        if (!guardOn) return undefined;
        const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onUnload);
        return () => window.removeEventListener('beforeunload', onUnload);
    }, [guardOn]);
    const leaveTo = (to) => { leavingRef.current = true; navigate(to); };
    const backToList = () => navigate('/reclamacoes-pontos');

    // ---- envio
    const submit = async (andNext = false) => {
        setSending(true);
        try {
            const items = Object.entries(draft.items).map(([item, d]) => ({
                item: Number(item),
                condition: d.denied ? undefined : d.condition,
                score: d.denied ? undefined : d.score,
                justification: d.justification || undefined,
                denied: !!d.denied,
            }));
            const r = await apiService.pointsDisputeSubmit({
                userId, subdomain: sd, transcription_id: tid,
                dispute_id: kase.dispute?.dispute_id, items, comments_answer: draft.answer || null,
            });
            if (!r || r.success !== true) throw new Error('resposta inesperada do envio');
            clearDraft(sd, tid);
            if (andNext) {
                let next = null;
                try { next = await findNextPending({ userId, subdomain: sd, currentTid: tid }); } catch (e) { next = null; }
                if (next) {
                    notify('Revisão enviada ✓ — abrindo a próxima', 'success');
                    leaveTo(`/reclamacoes-pontos/${encodeURIComponent(sd)}/${next}/perguntas`);
                    return;
                }
                notify('Revisão enviada ✓ — não há mais pendentes nesta lista', 'success');
            } else {
                notify('Revisão enviada ao vendedor ✓', 'success');
            }
            setTimeout(() => leaveTo('/reclamacoes-pontos'), 900);
        } catch (e) {
            const st = e.response?.status;
            setModal({ kind: 'error', text: e.response?.data?.detail || e.message || 'Erro ao enviar a revisão', conflict: st === 409 });
        } finally {
            setSending(false);
        }
    };

    // ---------------------------------------------------------------- render
    if (loadError) {
        return (
            <div className="vr-page"><div className="vr-list">
                <button className="vr-btn" onClick={() => navigate('/reclamacoes-pontos')}>‹ Voltar às reclamações</button>
                <div className="vr-card" style={{ marginTop: 14 }}>{loadError}</div>
            </div></div>
        );
    }
    if (!kase) return <div className="vr-page"><div className="vr-list">Carregando reclamação…</div></div>;

    const fb = kase.feedback || {};
    const dispute = kase.dispute || {};
    const likeLabel = fb.likes === -1 ? '👎 Não gostou' : fb.likes === 1 ? '👍 Gostou' : 'Sem avaliação';
    const isDispute = kase.kind === 'dispute';
    const isDislike = kase.kind === 'dislike';
    // comentário da reclamação: o do VAR; no dislike sem VAR, o comentário do feedback
    const claimComment = ((isDispute ? dispute.comment : fb.comments) || '').trim();
    // áudio gravado no Discordo (no lugar ou além do texto); a transcrição vira o comentário
    const claimAudio = isDispute ? dispute.audio : null;
    const claimAudioSrc = claimAudio
        ? `/api/audio-proxy/${claimAudio.audio_comment_id}${claimAudio.bucket_name ? `?bucket_name=${encodeURIComponent(claimAudio.bucket_name)}` : ''}`
        : null;
    const claimAudioPlayer = claimAudio ? (
        <span className="vr-vendor-audio">
            <span className="vr-vendor-label">🎙 Áudio do vendedor:</span>
            <audio className="vr-audio" controls preload="none" src={claimAudioSrc}>sem suporte a áudio</audio>
            {!claimAudio.transcribed && <em className="vr-muted vr-small">transcrição ainda não chegou</em>}
        </span>
    ) : null;
    const headStatus = !readOnly ? 'Pendente' : isDispute ? (STATUS_LABEL[dispute.status] || 'Revisada') : 'Respondida';
    const headStatusCls = !readOnly ? 'pending' : isDispute ? (dispute.status || 'denied') : 'granted';
    const sidePanel = step !== 'resumo' ? (
        <aside className="vr-side" style={{ top: stickyH, height: Math.max(240, (viewH || window.innerHeight) - stickyH - footerH) }}>
            <div className="vr-side-head">
                <button className={`vr-side-tab ${side === 'tx' ? 'active' : ''}`} onClick={() => openSide('tx')}>Transcrição</button>
                <button className={`vr-side-tab ${side === 'ai' ? 'active' : ''}`} onClick={() => openSide('ai')}>Feedback da IA</button>
                <button className={`vr-side-tab ${side === 'data' ? 'active' : ''}`} onClick={() => openSide('data')}>Dados</button>
                {side === 'tx' && txData && txData.text && (
                    <button className="vr-btn vr-btn-sm vr-side-copy" onClick={copyTranscript} title="Copiar a transcrição">📋 Copiar</button>
                )}
            </div>
            <div className="vr-side-body">
                {side === 'tx' && (txData === undefined ? 'Carregando…' : txData.error ? txData.error : (
                    <>
                        {txData.context && <div className="vr-muted vr-small" style={{ marginBottom: 8 }}>Transcrição com contexto anonimizado.</div>}
                        {!txData.text && <div className="vr-muted">Sem transcrição.</div>}
                        {txData.parsed ? txData.parsed.map((l, i) => (
                            <div key={i} className={`vr-tx-line ${l.speaker !== null ? `vr-tx-sp${l.speaker % 4}` : ''}`}>
                                <span className="vr-tx-time">{l.time || ''}</span>
                                <div>
                                    {(l.speaker !== null || l.label) && <div className="vr-tx-speaker">{l.speaker !== null ? `Falante ${l.speaker + 1}` : l.label}</div>}
                                    <div className="vr-tx-text">{l.text}</div>
                                </div>
                            </div>
                        )) : (txData.text && <div className="vr-tx-raw">{txData.text}</div>)}
                    </>
                ))}
                {side === 'ai' && (
                    <>
                        <div className="vr-label">Feedback</div>
                        <AiFeedback value={kase.ai_feedback?.feedback}
                            empty="A IA não gerou feedback para este atendimento." />
                        <div className="vr-label">Feedback longo</div>
                        <AiFeedback value={kase.ai_feedback?.long_feedback}
                            empty="A IA não gerou feedback longo para este atendimento (nem toda empresa ou loja tem o feedback longo ativo)." />
                    </>
                )}
                {side === 'data' && (extData === undefined ? 'Carregando…' : extData.error ? extData.error : (
                    Object.entries(extData.data).filter(([k]) => !HIDDEN_CHILDREN.has(k)).map(([k, v]) => (
                        <div key={k} className="vr-acc">
                            <div className="vr-acc-head" onClick={() => setOpenChild((p) => ({ ...p, [k]: !p[k] }))}>
                                <span className={`vr-chevron ${openChild[k] ? 'open' : ''}`}>›</span>
                                {CHILD_LABELS[k] || k}
                                {extData.models?.[k] && <span className="vr-muted vr-small">🤖 {extData.models[k]}</span>}
                            </div>
                            {openChild[k] && <div className="vr-acc-body">{v ? renderChild(k, v) : <span className="vr-muted">—</span>}</div>}
                        </div>
                    ))
                ))}
            </div>
        </aside>
    ) : null;

    // ---- etapa 1: perguntas
    const renderQuestion = (q) => {
        const i = qinfo(q);
        const meta = behMeta[q.behavior_key] || {};
        const open = !!expanded[q.item];
        const stateLabel = { up: '↑ alterada', down: '↓ alterada', changed: 'alterada', denied: '⊘ negada', justified: readOnly ? 'revisada' : 'justificada', none: '' }[i.state];
        const isBinary = i.earn === true;
        const lock = readOnly || !!i.d.denied;
        return (
            <div key={q.item} className={`vr-q st-${i.state}`}>
                <div className="vr-q-row" onClick={() => setExpanded((p) => ({ ...p, [q.item]: !open }))}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="vr-q-text">
                            {q.question}
                            {open && <>{' '}<Info text={q.condition_text ? `Condição: ${q.condition_text}` : null} /></>}
                        </div>
                        <div className="vr-chips" style={{ marginTop: 6 }}>
                            {q.behavior_key && (
                                <span className="vr-chip" style={meta.color ? { background: meta.color, color: '#fff' } : undefined}>
                                    {meta.icon ? `${meta.icon} ` : ''}{meta.display_name || q.behavior_key}{i.claimed ? ' · reclamado' : ''}
                                </span>
                            )}
                            {q.category && <span className="vr-chip">{q.category}</span>}
                        </div>
                    </div>
                    <div className="vr-q-side">
                        {stateLabel && <span className="vr-q-state">{stateLabel}</span>}
                        {i.earn === true && <span className="vr-calc"><strong>{fmt(i.eNew)}</strong> de {fmt(i.pts)} pt{i.eNew !== i.e0 ? ` (IA: ${fmt(i.e0)})` : ''}</span>}
                        <span className={`vr-chevron ${open ? 'open' : ''}`}>›</span>
                    </div>
                </div>
                {open && (
                    <div className="vr-q-body">
                        <div className="vr-label">Análise da IA <Info text={`Peso: ${fmt(q.weight)} · Confiança: ${i.sp.confidence ?? '—'}${i.sp.confidence != null ? '%' : ''}`} /></div>
                        <div className="vr-box">{i.sp.evidence || '—'}</div>
                        <div className="vr-label">Revisão</div>
                        <div className="vr-controls">
                            <Switch on={i.cond !== false} disabled={lock} label="Aplicável"
                                onChange={(v) => setItem(q.item, { condition: v === (i.base.cond !== false) ? undefined : v,
                                    score: v ? (i.d.score) : undefined })} />
                            {isBinary ? (
                                <Switch on={i.cond !== false && Number(i.score) > 0} disabled={lock || i.cond === false} label="Cumpriu"
                                    onChange={(v) => setItem(q.item, { score: (v ? 1 : 0) === Number(i.base.score ?? -1) ? undefined : (v ? 1 : 0) })} />
                            ) : (
                                <label className="vr-switch">Nota{' '}
                                    <input type="number" min="0" max="10" step="1" className="vr-score-input"
                                        value={i.cond === false ? '' : (i.score ?? '')} disabled={lock || i.cond === false}
                                        onChange={(e) => {
                                            const v = e.target.value === '' ? undefined : Math.max(0, Math.min(10, Number(e.target.value)));
                                            setItem(q.item, { score: v === Number(i.base.score) ? undefined : v });
                                        }} />
                                </label>
                            )}
                            <span className="vr-calc">
                                {i.earn === true
                                    ? <>Pontos: {fmt(i.pts)} × {i.cond === false ? 'não aplicável' : `nota ${fmt(i.score)}`} = <strong>{fmt(i.eNew)} pt</strong> <span className="vr-muted">(IA: {fmt(i.e0)})</span></>
                                    : 'Esta pergunta não gera pontos (só entra na nota).'}
                            </span>
                        </div>
                        {readOnly && i.rev && (
                            <><div className="vr-label">Justificativa da revisão</div>
                                <div className="vr-box">{(i.rev.justification || '').trim() || <span className="vr-muted">— sem justificativa —</span>}</div></>
                        )}
                        {!readOnly && (
                            <>
                                <div className="vr-label">Justificativa da revisão</div>
                                <textarea className="vr-textarea" placeholder="Por que manteve ou alterou (opcional, mas recomendado)…"
                                    value={i.d.justification || ''} onChange={(e) => setItem(q.item, { justification: e.target.value })} />
                                {i.d.denied && (
                                    <div className="vr-q-actions">
                                        <button className="vr-btn vr-btn-sm" onClick={() => setItem(q.item, { denied: undefined })}>Desfazer negação</button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const untouchedClaimed = details.filter((q) => q.behavior_key && claimedKeys.has(q.behavior_key) && qinfo(q).earn === true && !draft.items[q.item]);
    const noJustification = Object.entries(draft.items).filter(([, d]) => !d.justification).map(([item]) => details.find((q) => String(q.item) === item)).filter(Boolean);
    const deltaClass = (a, b) => (b > a ? 'vr-delta-up' : b < a ? 'vr-delta-down' : 'vr-delta-eq');
    const deltaArrow = (a, b) => (b > a ? '↑' : b < a ? '↓' : '=');
    const behName = (k) => (behMeta[k] || {}).display_name || k;

    const BehRow = ({ b, from }) => {
        const meta = behMeta[b.key] || {};
        const a = b[from];
        const d = b.now > a ? 'd-up' : b.now < a ? 'd-down' : 'd-eq';
        return (
            <div className={`vr-beh-row ${d}`}>
                <span className="vr-beh-dot" style={{ background: meta.color || '#e5e7eb', opacity: 0.6 }}>{meta.icon || ''}</span>
                <span className="vr-beh-name">{behName(b.key)}</span>
                <span className={deltaClass(a, b.now)}>{fmt(a)} → {fmt(b.now)} {deltaArrow(a, b.now)}</span>
                <span className="vr-muted vr-small">de {fmt(b.max)}</span>
            </div>
        );
    };
    const BehSummaryList = ({ from = 'base' }) => {
        if (behaviorSummary.length === 0) return <div className="vr-muted">Nenhum comportamento pontuável.</div>;
        if (kase.kind !== 'dispute') return behaviorSummary.map((b) => <BehRow key={b.key} b={b} from={from} />);
        const claimed = behaviorSummary.filter((b) => claimedKeys.has(b.key));
        const others = behaviorSummary.filter((b) => !claimedKeys.has(b.key));
        return (<>
            <div className="vr-beh-section">Reclamados ({claimed.length})</div>
            {claimed.length ? claimed.map((b) => <BehRow key={b.key} b={b} from={from} />)
                : <div className="vr-muted vr-small">Nenhum comportamento reclamado com pontuação nesta avaliação.</div>}
            {others.length > 0 && <>
                <div className="vr-beh-section">Não reclamados ({others.length})</div>
                {others.map((b) => <BehRow key={b.key} b={b} from={from} />)}
            </>}
        </>);
    };

    let content = null;
    let footer = null;
    if (step === 'perguntas') {
        content = (
            <>
                <div className="vr-qfilters">
                    {[['all', 'Todos'], ['claimed', 'Reclamados'], ['behavior', 'Com comportamento']].map(([k, l]) => (
                        <button key={k} className={`vr-pill-filter ${qFilter === k ? 'active' : ''}`} onClick={() => setQFilter(k)}>{l}</button>
                    ))}
                    <span className="vr-muted vr-small" style={{ marginLeft: 'auto' }}>
                        {details.length} perguntas · {Object.keys(draft.items).length} com revisão neste rascunho
                    </span>
                </div>
                {details.length === 0 && <div className="vr-card">Este atendimento não tem avaliação revisável. Siga para a resposta ao vendedor.</div>}
                {grouped.map((g) => (
                    <div key={g.key || '_none'}>
                        <div className="vr-group-title">
                            {g.key ? <>{(behMeta[g.key] || {}).icon || ''} {behName(g.key)}{claimedKeys.has(g.key) ? <span className="vr-chip vr-chip-pending">reclamado</span> : null}</> : 'Sem comportamento'}
                        </div>
                        {g.items.map(renderQuestion)}
                    </div>
                ))}
                {grouped.length === 0 && details.length > 0 && <div className="vr-empty">Nenhuma pergunta neste filtro.</div>}
            </>
        );
        if (readOnly) {
            footer = (<><span className="vr-footer-info">Revisão já enviada — só leitura.</span>
                <button className="vr-btn vr-btn-primary" onClick={() => goStep('resposta')}>Próximo ›</button></>);
        } else {
            // barra sempre visível; "Descartar" só faz sentido com alteração (fica desabilitado sem)
            footer = (<>
                <button className="vr-btn vr-btn-danger-outline" disabled={!hasAnything} onClick={() => setModal({ kind: 'discard' })}>Descartar alterações</button>
                <span className="vr-footer-info">{hasItemChanges
                    ? `${Object.keys(draft.items).length} pergunta(s) com revisão · rascunho salvo neste navegador`
                    : 'Nenhuma pergunta alterada ainda · não alterar = manter o que a IA avaliou'}</span>
                <button className="vr-btn vr-btn-primary" onClick={() => (details.length === 0 ? goStep('resposta') : setModal({ kind: 'review' }))}>Salvar revisão</button>
            </>);
        }
    } else if (step === 'resposta') {
        const ans = draft.answer ?? '';
        content = (
            <div className="vr-card">
                <h3>Resposta ao vendedor</h3>
                <p className="vr-muted vr-small" style={{ marginTop: 0 }}>Aparece no app em "Resultado do VAR". Só é enviada no último passo.</p>
                {readOnly ? (
                    <div className="vr-box">{(fb.comments_answer || '').trim() || <em className="vr-muted">Sem detalhes sobre a alteração.</em>}</div>
                ) : (
                    <textarea className="vr-answer" placeholder="Explique ao vendedor o que foi revisto e por quê…" value={ans}
                        onChange={(e) => persist({ ...draft, answer: e.target.value })} />
                )}
            </div>
        );
        footer = (<>
            <button className="vr-btn" onClick={() => goStep('perguntas')}>‹ Voltar</button>
            <span className="vr-footer-info">{readOnly ? 'Só leitura.' : 'Rascunho salvo neste navegador'}</span>
            {readOnly ? <button className="vr-btn vr-btn-primary" onClick={() => goStep('resumo')}>Próximo ›</button>
                : <button className="vr-btn vr-btn-primary" onClick={() => (ans.trim() ? goStep('resumo') : setModal({ kind: 'emptyAnswer' }))}>Salvar resposta</button>}
        </>);
    } else {
        const answerText = readOnly ? fb.comments_answer : draft.answer;
        content = (
            <>
                <div className="vr-card">
                    <h3>Mudança de pontos</h3>
                    <div className="vr-tabs">
                        <button className={`vr-pill-filter ${summaryTab === 'beh' ? 'active' : ''}`} onClick={() => setSummaryTab('beh')}>Por comportamento</button>
                        <button className={`vr-pill-filter ${summaryTab === 'q' ? 'active' : ''}`} onClick={() => setSummaryTab('q')}>Por pergunta</button>
                    </div>
                    {summaryTab === 'beh' ? <BehSummaryList from="orig" /> : (
                        details.filter((q) => draft.items[q.item] || (readOnly && qinfo(q).rev)).map((q) => {
                            const i = qinfo(q);
                            return (
                                <div key={q.item} className="vr-beh-row">
                                    <span className="vr-beh-name" style={{ fontWeight: 500 }}>{q.question}</span>
                                    {i.d.denied && <span className="vr-chip vr-chip-denied">negada</span>}
                                    {i.earn === true ? <span className={deltaClass(i.e0, i.eNew)}>{fmt(i.e0)} → {fmt(i.eNew)} {deltaArrow(i.e0, i.eNew)}</span>
                                        : <span className="vr-muted vr-small">nota {fmt(i.orig.score)} → {fmt(i.score)}</span>}
                                </div>
                            );
                        })
                    )}
                </div>
                <div className="vr-card">
                    <h3>{isDislike ? 'Reclamação do vendedor sobre o feedback' : 'Reclamação do vendedor'}</h3>
                    {isDislike && <div className="vr-small vr-muted" style={{ marginBottom: 8 }}>👎 no feedback, sem reclamação de pontos (VAR).</div>}
                    <div className="vr-chips" style={{ marginBottom: 8 }}>
                        {(dispute.behaviors || []).map((b) => <span key={b.behavior_key || b.raw_label} className="vr-chip vr-chip-pending">{b.display_name || b.raw_label}</span>)}
                    </div>
                    <div className="vr-box">{claimComment || <em className="vr-muted">Sem comentário.</em>}</div>
                    {claimAudioPlayer && <div style={{ marginTop: 8 }}>{claimAudioPlayer}</div>}
                </div>
                <div className="vr-card">
                    <h3>Resposta ao vendedor</h3>
                    <div className="vr-box">{(answerText || '').trim() || <em className="vr-muted">Sem detalhes sobre a alteração.</em>}</div>
                </div>
            </>
        );
        footer = (<>
            <button className="vr-btn" onClick={() => goStep('resposta')}>‹ Voltar</button>
            <span className="vr-footer-info">{readOnly ? `Revisão enviada${kase.reviewed_by ? ` por ${kase.reviewed_by}` : ''} em ${brDateTime(kase.reviewed_at)}.` : 'Só ao enviar o vendedor passa a ver o resultado.'}</span>
            {!readOnly && <button className="vr-btn vr-btn-success" disabled={sending} onClick={() => setModal({ kind: 'send' })}>{sending ? 'Enviando…' : 'Enviar revisão'}</button>}
        </>);
    }

    return (
        <div className="vr-page vr-review">
            {toast && <div className={`vr-toast ${toast.type}`}>{toast.msg}</div>}
            <div className="vr-sticky" ref={stickyRef}>
                <div className="vr-head">
                    <button className="vr-back" onClick={backToList} title="Voltar às reclamações">‹</button>
                    <div className="vr-head-main">
                        <div className="vr-head-title">
                            <h1>{kase.salesman_name || 'Vendedor'}</h1>
                            {isDispute
                                ? <span className="vr-chip vr-chip-var">VAR · reclamação de pontuação</span>
                                : isDislike
                                    ? <span className="vr-chip vr-chip-dislike">👎 Reclamação sobre o feedback (sem VAR)</span>
                                    : <span className="vr-chip">Sem reclamação</span>}
                            <span className={`vr-chip vr-chip-${headStatusCls}`}>{headStatus}</span>
                        </div>
                        <div className="vr-head-facts">
                            <span>{kase.company_name || sd} · {kase.store_name || '—'}</span>
                            <span>Atendimento: <strong>{brDateTime(kase.attendance_at)}</strong></span>
                            <span>Classificação: <strong>{kase.classification || '—'}</strong>{kase.original_classification && kase.original_classification !== kase.classification ? ` (original: ${kase.original_classification})` : ''}</span>
                            <span>Nota original: <strong>{fmt(kase.original_final_score)}</strong></span>
                            {isDispute && <span>Pontos em jogo: <strong>{fmt(kase.claimed_open_points)}</strong></span>}
                        </div>
                        <div className="vr-head-ids">feedback {fb.id ?? '—'} · transcrição {tid}</div>
                    </div>
                    <div className="vr-head-tools">
                        {audioUrl
                            ? <audio className="vr-audio" controls autoPlay src={audioUrl}
                                onError={() => { setAudioUrl(null); audioMissing(); }}>sem suporte a áudio</audio>
                            : <button className="vr-btn" onClick={loadAudio}>▶ Ouvir{kase.restricted_admin ? ' (anonimizado)' : ''}</button>}
                    </div>
                </div>
                <div className="vr-vendor">
                    <span className="vr-vendor-like"><span className="vr-vendor-label">{isDislike ? 'Reclamação sobre o feedback:' : 'Reclamação:'}</span>{likeLabel}</span>
                    <span className="vr-vendor-comment">
                        <span className="vr-vendor-label">Comentário:</span>
                        {claimComment ? <>“{claimComment}”</> : <em>Sem comentário na reclamação.</em>}
                    </span>
                    {claimAudioPlayer}
                    <div className="vr-chips">
                        {(dispute.behaviors || []).map((b) => <span key={b.behavior_key || b.raw_label} className="vr-chip vr-chip-pending">{b.display_name || b.raw_label}</span>)}
                    </div>
                </div>
                {readOnly && <div className="vr-readonly-banner">Revisão já enviada{kase.reviewed_by ? ` por ${kase.reviewed_by}` : ''} em {brDateTime(kase.reviewed_at)} — só leitura.</div>}
                <div className="vr-steps">
                    {STEPS.map((s) => (
                        <button key={s.key} className={`vr-step ${step === s.key ? 'active' : ''}`}
                            disabled={!canOpenStep(s.key)} onClick={() => goStep(s.key)}>{s.label}</button>
                    ))}
                </div>
            </div>

            <div className="vr-body">
                {sidePanel}
                <main className="vr-main">{content}</main>
            </div>
            {footer && <div className="vr-footer" ref={footerRef}>{footer}</div>}

            {modal?.kind === 'discard' && (
                <Modal title="Descartar alterações?" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="vr-btn vr-btn-danger-outline" onClick={() => { clearDraft(sd, tid); setDraft({ items: {}, answer: null, stage: 'perguntas' }); setModal(null); }}>Descartar tudo</button></>}>
                    Todas as alterações, justificativas e a resposta deste rascunho serão apagadas. <strong>Esta ação não pode ser desfeita.</strong>
                </Modal>
            )}
            {modal?.kind === 'review' && (
                <Modal large title="Conferir a revisão" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Voltar às perguntas</button>
                        <button className="vr-btn vr-btn-primary" onClick={() => { setModal(null); goStep('resposta'); }}>Continuar para a resposta ›</button></>}>
                    {untouchedClaimed.length > 0 && (
                        <div className="vr-mbox warn">
                            <h4>Perguntas reclamadas sem decisão ({untouchedClaimed.length})</h4>
                            <div className="vr-small vr-muted" style={{ marginBottom: 6 }}>Se seguir assim, elas ficam como a IA avaliou (negadas).</div>
                            <ul>{untouchedClaimed.map((q) => <li key={q.item}>{q.question}</li>)}</ul>
                        </div>
                    )}
                    {noJustification.length > 0 && (
                        <div className="vr-mbox info">
                            <h4>Sem justificativa ({noJustification.length})</h4>
                            <ul>{noJustification.map((q) => <li key={q.item}>{q.question}</li>)}</ul>
                        </div>
                    )}
                    <div className="vr-mbox">
                        <h4>Pontos por comportamento</h4>
                        <BehSummaryList from="base" />
                    </div>
                </Modal>
            )}
            {modal?.kind === 'emptyAnswer' && (
                <Modal title="Seguir sem resposta?" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Escrever resposta</button>
                        <button className="vr-btn vr-btn-primary" onClick={() => { setModal(null); goStep('resumo'); }}>Seguir sem resposta</button></>}>
                    {kase.kind === 'dispute'
                        ? <>A resposta está em branco. No app, o resultado do VAR vai aparecer como <em>“Sem detalhes sobre a alteração.”</em></>
                        : <>A resposta está em branco: o vendedor não recebe nenhuma resposta ao dislike, só a revisão das perguntas (se houver).</>}
                </Modal>
            )}
            {modal?.kind === 'send' && (
                <Modal title="Enviar a revisão?" onClose={() => setModal(null)}
                    footer={<><button className="vr-btn" onClick={() => setModal(null)}>Cancelar</button>
                        <button className="vr-btn vr-btn-success" onClick={() => { setModal(null); submit(false); }}>Enviar</button>
                        {readNavContext(sd) && (
                            <button className="vr-btn vr-btn-primary" onClick={() => { setModal(null); submit(true); }}
                                title="Envia esta revisão e abre o próximo pendente da lista de onde você veio">Enviar e abrir a próxima ›</button>
                        )}</>}>
                    A partir do envio, o vendedor vê o resultado e a resposta no app, e a revisão deste atendimento não pode mais ser editada.
                </Modal>
            )}
            {blocker.state === 'blocked' && (
                <Modal wide title="Sair da revisão?" onClose={() => blocker.reset()}
                    footer={<><button className="vr-btn" onClick={() => blocker.reset()}>Continuar revisando</button>
                        <button className="vr-btn vr-btn-danger-outline" onClick={() => { clearDraft(sd, tid); blocker.proceed(); }}>Descartar e sair</button>
                        <button className="vr-btn vr-btn-primary" onClick={() => blocker.proceed()}>Sair e manter rascunho</button></>}>
                    Esta revisão ainda não foi enviada. O rascunho fica salvo <strong>neste navegador por 7 dias</strong>; se descartar, as alterações são perdidas e <strong>não podem ser recuperadas</strong>.
                </Modal>
            )}
            {modal?.kind === 'error' && (
                <Modal title={modal.conflict ? 'Não foi possível enviar' : 'Erro ao enviar'} onClose={() => setModal(null)}
                    footer={modal.conflict
                        ? <><button className="vr-btn" onClick={() => setModal(null)}>Fechar</button>
                            <button className="vr-btn vr-btn-primary" onClick={() => { setModal(null); clearDraft(sd, tid); setKase(null); load(); }}>Ver a revisão enviada</button></>
                        : <button className="vr-btn vr-btn-primary" onClick={() => setModal(null)}>Fechar</button>}>
                    {modal.text}
                </Modal>
            )}
        </div>
    );
}

// Uma instância por atendimento: "Enviar e abrir a próxima" navega para a mesma rota com outro
// :transcriptionId, e o estado (caso, rascunho, painel, áudio) tem de começar do zero.
function ReclamacaoRevisaoRoute(props) {
    const { subdomain, transcriptionId } = useParams();
    return <ReclamacaoRevisao key={`${subdomain}:${transcriptionId}`} {...props} />;
}

export default ReclamacaoRevisaoRoute;
